'use client';

import { useState, useEffect, useCallback } from 'react';
import { adminKycApi } from '../api';
import { PendingDocument } from '../types';

interface UsePendingDocumentsReturn {
  documents: PendingDocument[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function usePendingDocuments(
  tenantSlug: string,
  limit: number = 50
): UsePendingDocumentsReturn {
  const [documents, setDocuments] = useState<PendingDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchDocuments = useCallback(async () => {
    if (!tenantSlug) return;

    try {
      setIsLoading(true);
      setError(null);
      const result = await adminKycApi.getPendingDocuments(tenantSlug, limit);
      setDocuments(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch pending documents'));
    } finally {
      setIsLoading(false);
    }
  }, [tenantSlug, limit]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  return {
    documents,
    isLoading,
    error,
    refetch: fetchDocuments,
  };
}
